import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Badge de shadcn. Regla de la escala del panel (`escala-panel.ts`): chips y etiquetas van
 * rectangulares (`shape="rect"`); para estados con tono usar `StatusPill shape="rect"`.
 */
const badgeVariants = cva(
  "inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
      /**
       * Escala del panel (`escala-panel.ts`): chips y estados rectangulares (`rect`, rounded-md).
       * `pill` se conserva como default para no cambiar los ~1100 usos sin revisarlos.
       */
      shape: {
        pill: "rounded-full",
        rect: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      shape: "pill",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

// forwardRef: usado como asChild de Tooltip/Popover en varias pantallas - sin la ref
// real del DOM, Radix no puede anclar el contenido y este no se llega a posicionar/mostrar.
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(({ className, variant, shape, ...props }, ref) => {
  return <div ref={ref} className={cn(badgeVariants({ variant, shape }), className)} {...props} />;
});
Badge.displayName = "Badge";

export { Badge, badgeVariants };
